import { normalizedFromNestedStructured } from "@/shared";
import { EMPTY_SIMPLE_COMBO } from "@/shared/data";
import type { EntityType, StaticEntityType } from "@config";
import { nanoid, type Dispatch } from "@reduxjs/toolkit";
import { type AppDispatch, useAppDispatch } from "@store";
import { createEntity, editEntity } from "@store/story";
import { setBoundOrFieldValue } from "@store/story/thunks/set-bound-or-field-value";
import { deferButtonBorderToDesignSystem } from "./defer-button-border-to-design-system";
import { useSlidesLimit } from "./use-slides-limit";

const SWAP_MAPPING = {
  Image: "src",
  Logo: "src",
  Icon: "icon",
  Video: "src",
} as const;

const SWAP_BINDING_KEY: Partial<
  Record<keyof typeof SWAP_MAPPING, string>
> = {
  Image: "dynamicVariableSrc",
  Logo: "dynamicVariableSrc",
  Video: "dynamicVariableSrc",
};

type CreateEntityPayload = {
  payload: StaticEntityType;
  entity: {
    id: string;
    type: string;
    parentId?: string | undefined;
    name?: string | undefined;
  };
  direction: "top" | "bottom" | "replace";
  isPlaceholder?: boolean;
};

export const useCreateEntity = () => {
  const dispatch = useAppDispatch();
  const { isLimitReached, lockAction } = useSlidesLimit();

  const handleCreateEntity = ({
    payload: rawPayload,
    entity,
    direction,
    isPlaceholder,
  }: CreateEntityPayload) => {
    if (!rawPayload) return;
    // Buttons arriving from the elements library carry a hardcoded 1px
    // border width that would otherwise override the design system.
    const payload = deferButtonBorderToDesignSystem(rawPayload);
    const { type } = entity;

    const mapped = {
      element: createElement,
      combo: createCombo,
      slide: createSlide,
    };

    if (isPlaceholder) {
      return createForPlaceholder({
        payload,
        entity,
        direction,
        dispatch,
      });
    }

    mapped[type as keyof typeof mapped]({
      payload,
      entity,
      direction,
      dispatch,
      isLimitReached,
      lockAction,
    });
  };

  return {
    handleCreateEntity,
  };
};

// =========== UTILS ===========
const createElement = ({
  payload,
  entity,
  direction,
  dispatch,
}: CreateEntityPayload & { dispatch: AppDispatch }) => {
  if (direction === "replace") {
    const settings = payload.settings as Record<string, any>;
    const swapKey = SWAP_MAPPING[settings.name as keyof typeof SWAP_MAPPING];
    if (!swapKey) return;

    const additionalData: Record<string, unknown> = {};

    if (settings.name === "Video") {
      additionalData.type = "Library";
    }

    if (settings.name === "Image" || settings.name === "Logo") {
      additionalData.metadata = {
        width: settings.metadata?.width,
        height: settings.metadata?.height,
      };
    }

    const bindingKey =
      SWAP_BINDING_KEY[settings.name as keyof typeof SWAP_MAPPING];

    if (bindingKey) {
      dispatch(
        setBoundOrFieldValue({
          entityId: entity.id,
          key: swapKey,
          bindingKey,
          value: settings[swapKey],
          extra: additionalData,
        }),
      );
    } else {
      dispatch(
        editEntity({
          id: entity.id,
          data: { ...additionalData, [swapKey]: settings[swapKey] },
        }),
      );
    }
    return;
  }

  const element = {
    id: nanoid(),
    type: "element",
    parentId: entity.parentId,
    childIds: [],
    settings: payload.settings,
  } as EntityType;

  dispatch(
    createEntity({
      entityEntryRootId: element.id,
      entities: { [element.id]: element },
      parentId: element.parentId as string,
      placement: direction,
      id: entity.id,
      feature: "elements-library",
    }),
  );
};

// Recursively force VTimeline combos to maxWidth: "medium" so saved
// templates with stale `narrow` (or invalid `large`) get a sensible
// horizontal default at insertion. User can still override via the
// maxWidth control.
const overrideTimelineMaxWidth = (entity: StaticEntityType): StaticEntityType => {
  const settings = entity.settings as Record<string, unknown> | undefined;
  const next: StaticEntityType =
    settings?.name === "VTimeline"
      ? { ...entity, settings: { ...settings, maxWidth: "medium" } }
      : entity;
  if (next.children?.length) {
    return {
      ...next,
      children: next.children.map(overrideTimelineMaxWidth),
    };
  }
  return next;
};

const createCombo = ({
  payload,
  entity,
  direction,
  dispatch,
}: CreateEntityPayload & { dispatch: Dispatch; isPlaceholder?: boolean }) => {
  const { entities, root } = normalizedFromNestedStructured(
    [{ children: [overrideTimelineMaxWidth(payload)] }],
    null,
    "combo",
  );
  dispatch(
    createEntity({
      entityEntryRootId: root,
      entities: entities,
      id: entity.id,
      placement: direction as "top" | "bottom",
      parentId: entity.parentId as string,
      feature: "elements-library",
    }),
  );
};

const createSlide = ({
  payload,
  entity,
  direction,
  dispatch,
  isLimitReached,
  lockAction,
}: CreateEntityPayload & {
  dispatch: Dispatch;
  isLimitReached: boolean;
  lockAction: () => void;
}) => {
  if (isLimitReached) return lockAction();
  const slideElement = document
    .querySelector(`[data-layer-id="${entity.id}"]`)
    ?.closest('[data-layer-type="slide"]') as HTMLElement;
  const slideId = slideElement?.dataset.layerId ?? "";
  const { entities, root } = normalizedFromNestedStructured(
    [overrideTimelineMaxWidth(payload)],
    "root",
    "slide",
  );
  dispatch(
    createEntity({
      entityEntryRootId: root,
      entities: entities,
      id: slideId,
      placement: direction as "top" | "bottom",
      feature: "elements-library",
      parentId: "root",
    }),
  );
};

const createForPlaceholder = ({
  payload,
  entity,
  dispatch,
}: CreateEntityPayload & { dispatch: Dispatch }) => {
  const parent = document.querySelector(
    `[data-layer-id="${entity.parentId}"]`,
  ) as HTMLElement;
  const type = parent?.dataset.layerType;
  if (type === "group") {
    createElement({
      payload,
      entity,
      direction: "bottom",
      dispatch,
    });
  }
  if (type === "slide") {
    createCombo({
      payload: EMPTY_SIMPLE_COMBO(payload),
      entity,
      direction: "bottom",
      dispatch,
    });
  }
};

// =========== UTILS ===========
